import BetLockedModal from "./components/BetLockedModal";
import confetti from "canvas-confetti";
import Profile from "./Profile";
import Multiplayer from "./Multiplayer";
import Matches from "./Matches";
import Fantasy11 from "./Fantasy11";
import Mines from "./Mines";
import LiveInPlayDashboard from "./LiveInPlayDashboard";
import React, { useState, useEffect, useRef, useCallback } from "react";
import ThemeSwitcher from "./ThemeSwitcher";
import ProfileMusicCard from "./components/ProfileMusicCard";
import ThemeMusicPlayer from './components/ThemeMusicPlayer';
import AdminPanel from "./AdminPanel";
import OddsApiHub from "./OddsApiHub";
import Shop from "./Shop";
import "./App.css";
import { auth } from "./firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from "firebase/auth";

// ── TOAST SYSTEM ─────────────────────────────────────────────────────────────
// Each toast: { id, message, type: "success"|"error"|"info"|"warning", emoji }
function ToastContainer({ toasts, onRemove }) {
  return (
    <div style={{
      position: "fixed", top: 70, right: 16, zIndex: 99999,
      display: "flex", flexDirection: "column", gap: 10,
      pointerEvents: "none",
    }}>
      {toasts.map(t => (
        <Toast key={t.id} toast={t} onRemove={onRemove} />
      ))}
    </div>
  );
}

function Toast({ toast, onRemove }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // mount → slide in
    const show = setTimeout(() => setVisible(true), 10);
    // auto-dismiss
    const hide = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(toast.id), 320);
    }, toast.duration || 3500);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []);

  const colors = {
    success: { bg: "#0d2b1f", border: "#1D9E75", text: "#22c55e", bar: "#1D9E75" },
    error: { bg: "#2b0d0d", border: "#E24B4A", text: "#f87171", bar: "#E24B4A" },
    info: { bg: "#0d1a2b", border: "#3b82f6", text: "#60a5fa", bar: "#3b82f6" },
    warning: { bg: "#2b1e0d", border: "#f4c430", text: "#fbbf24", bar: "#f4c430" },
  };
  const c = colors[toast.type] || colors.info;

  return (
    <div style={{
      background: c.bg,
      border: `1px solid ${c.border}55`,
      borderLeft: `3px solid ${c.border}`,
      borderRadius: 12,
      padding: "12px 16px",
      minWidth: 260,
      maxWidth: 340,
      pointerEvents: "all",
      cursor: "pointer",
      transform: visible ? "translateX(0)" : "translateX(120%)",
      opacity: visible ? 1 : 0,
      transition: "transform 0.32s cubic-bezier(0.34,1.56,0.64,1), opacity 0.32s ease",
      boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${c.border}22`,
      position: "relative",
      overflow: "hidden",
    }} onClick={() => { setVisible(false); setTimeout(() => onRemove(toast.id), 320); }}>
      {/* progress bar */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, height: 2,
        background: c.bar, borderRadius: "0 0 0 12px",
        animation: `toastProgress ${toast.duration || 3500}ms linear forwards`,
      }} />
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1.3 }}>{toast.emoji}</span>
        <div>
          {toast.title && (
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 2 }}>{toast.title}</div>
          )}
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>{toast.message}</div>
        </div>
      </div>
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const remove = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);
  const add = useCallback((opts) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-4), { id, ...opts }]);
  }, []);
  const toast = {
    success: (title, message, duration) => add({ type: "success", emoji: "✅", title, message, duration }),
    error: (title, message, duration) => add({ type: "error", emoji: "❌", title, message, duration }),
    info: (title, message, duration) => add({ type: "info", emoji: "ℹ️", title, message, duration }),
    warning: (title, message, duration) => add({ type: "warning", emoji: "⚠️", title, message, duration }),
    bet: (message, duration) => add({ type: "success", emoji: "🔒", title: "Bet Locked!", message, duration }),
    win: (message, duration) => add({ type: "success", emoji: "🏆", title: "You Won!", message, duration }),
    points: (message, duration) => add({ type: "warning", emoji: "💰", title: "Points Updated", message, duration }),
  };
  return { toasts, remove, toast };
}

// ── FLOATING POINTS ANIMATION ────────────────────────────────────────────────
function FloatingPoints({ floaters, navRef }) {
  return (
    <>
      {floaters.map(f => (
        <div key={f.id} style={{
          position: "fixed",
          top: f.y,
          right: f.x,
          zIndex: 99998,
          pointerEvents: "none",
          fontSize: 16,
          fontWeight: 800,
          color: f.positive ? "#22c55e" : "#f87171",
          textShadow: f.positive ? "0 0 12px rgba(34,197,94,0.8)" : "0 0 12px rgba(248,113,113,0.8)",
          animation: "floatUp 1.6s cubic-bezier(0.16,1,0.3,1) forwards",
          whiteSpace: "nowrap",
        }}>
          {f.positive ? "+" : ""}{f.amount.toLocaleString()} pts
        </div>
      ))}
    </>
  );
}

function useFloatingPoints() {
  const [floaters, setFloaters] = useState([]);
  const fire = useCallback((amount) => {
    const id = Date.now() + Math.random();
    // anchor near top-right navbar points display
    const x = 80 + Math.random() * 40;
    const y = 60 + Math.random() * 10;
    setFloaters(prev => [...prev.slice(-6), { id, amount, positive: amount > 0, x, y }]);
    setTimeout(() => setFloaters(prev => prev.filter(f => f.id !== id)), 1800);
  }, []);
  return { floaters, fire };
}

const API = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:5000"
  : (process.env.REACT_APP_API_URL || "http://localhost:5000");

const SPORTS = [
  { id: "cricket", name: "Cricket", emoji: "🏏", teams: ["India", "Australia", "England", "Pakistan"], available: true, gradientFrom: "#1a2e1a", gradientTo: "#0d1a0d", accentColor: "#22c55e", glowColor: "rgba(34,197,94,0.15)", tagline: "Live IPL Gaming" },
  { id: "football", name: "Football", emoji: "⚽", teams: ["Real Madrid", "Barcelona", "Man City", "PSG"], available: false, gradientFrom: "#1a1a2e", gradientTo: "#0d0d1a", accentColor: "#3b82f6", glowColor: "rgba(59,130,246,0.12)", tagline: "Premier League & More" },
  { id: "basketball", name: "Basketball", emoji: "🏀", teams: ["Lakers", "Warriors", "Bulls", "Celtics"], available: false, gradientFrom: "#2e1a0d", gradientTo: "#1a0d06", accentColor: "#f97316", glowColor: "rgba(249,115,22,0.12)", tagline: "NBA Action" },
  { id: "tennis", name: "Tennis", emoji: "🎾", teams: ["Djokovic", "Alcaraz", "Sinner", "Medvedev"], available: false, gradientFrom: "#2e2a0a", gradientTo: "#1a1806", accentColor: "#eab308", glowColor: "rgba(234,179,8,0.12)", tagline: "Grand Slams & Tours" },
];

// Hook: animates a number counting up from 0 to target
function useCountUp(target, duration = 1200, trigger = true) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const prevTarget = useRef(target);

  useEffect(() => {
    if (!trigger) { setDisplay(target); return; }
    const start = prevTarget.current === target ? 0 : display;
    prevTarget.current = target;
    const startTime = performance.now();
    const diff = target - start;

    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    }

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, trigger]);

  return display;
}

const fireConfetti = () => {
  confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#f0b429", "#e55a2b", "#22c55e", "#fff"] });
  setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.5 }, colors: ["#f0b429", "#fff"] }), 300);
};

function LoadingDots() {
  return (
    <span className="loading-dots">
      <span /><span /><span />
    </span>
  );
}

function Fantasy11BreakdownModal({ item, username, onClose }) {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchBreakdown() {
      try {
        const res = await fetch(`${API}/fantasy11-settle/breakdown/${username}/${item.matchId}`);
        const data = await res.json();
        if (res.ok) setBreakdown(data);
        else setError(data.message || "Could not load breakdown.");
      } catch {
        setError("Server error. Make sure this match has been settled.");
      }
      setLoading(false);
    }
    fetchBreakdown();
  }, [item.matchId, username]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: 16,
    }}>
      <div style={{
        background: "#0d1117", border: "1px solid #30363d", borderRadius: 16,
        width: "100%", maxWidth: 520, maxHeight: "85vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #30363d",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#ffd166" }}>🏏 Fantasy 11 Breakdown</div>
            <div style={{ fontSize: 12, color: "#7d8590", marginTop: 3 }}>{item.matchLabel}</div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "1px solid #30363d", color: "#7d8590",
            borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16,
          }}>×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {loading && <div style={{ textAlign: "center", color: "#7d8590", padding: 40 }}>Loading breakdown...</div>}

          {error && !loading && (
            <div style={{ background: "#300", border: "1px solid #a32d2d", borderRadius: 8, padding: "12px 16px", color: "#ff6b6b", fontSize: 13 }}>
              ⚠️ {error}
              <div style={{ marginTop: 8, fontSize: 12, color: "#7d8590" }}>This match may not have been settled yet. Check back after the match ends.</div>
            </div>
          )}

          {breakdown && !loading && (
            <>
              <div style={{
                background: "linear-gradient(135deg,#ffd16615,#ffd16605)",
                border: "1px solid #ffd16644", borderRadius: 12,
                padding: "14px 18px", marginBottom: 16,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 12, color: "#7d8590" }}>Total Fantasy Points</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: "#ffd166", lineHeight: 1 }}>{breakdown.totalPoints}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, color: "#7d8590" }}>Captain</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#e6edf3" }}>{breakdown.captain}</div>
                  <div style={{ fontSize: 12, color: "#7d8590", marginTop: 4 }}>Vice-Captain</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#e6edf3" }}>{breakdown.viceCaptain}</div>
                </div>
              </div>

              <div style={{ fontSize: 11, color: "#7d8590", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Player Breakdown</div>
              {breakdown.breakdown.map((p, i) => (
                <div key={p.name} style={{
                  background: p.isCaptain ? "#ffd16610" : p.isViceCaptain ? "#7B68EE10" : "#161b22",
                  border: `1px solid ${p.isCaptain ? "#ffd16644" : p.isViceCaptain ? "#7B68EE44" : "#30363d"}`,
                  borderRadius: 10, padding: "10px 14px", marginBottom: 8,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: "50%",
                      background: i === 0 ? "#ffd166" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#30363d",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700, color: i < 3 ? "#000" : "#7d8590", flexShrink: 0,
                    }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: "#e6edf3" }}>{p.name}</span>
                        {p.isCaptain && <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 99, background: "#ffd166", color: "#000" }}>C · 2×</span>}
                        {p.isViceCaptain && <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 99, background: "#7B68EE", color: "#fff" }}>VC · 1.5×</span>}
                        {!p.foundInScorecard && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 99, background: "#30363d", color: "#7d8590" }}>DNP</span>}
                      </div>
                      {p.foundInScorecard && (
                        <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                          {p.batting && (
                            <span style={{ fontSize: 10, color: "#00C896" }}>
                              🏏 {p.batting.runs}r ({p.batting.balls}b)
                              {p.batting.fours > 0 && ` · ${p.batting.fours}×4`}
                              {p.batting.sixes > 0 && ` · ${p.batting.sixes}×6`}
                              <span style={{ color: "#7d8590" }}> +{p.battingPts}pts</span>
                            </span>
                          )}
                          {p.bowling && (
                            <span style={{ fontSize: 10, color: "#FF6B6B" }}>
                              🎳 {p.bowling.wickets}w ({p.bowling.overs}ov)
                              <span style={{ color: "#7d8590" }}> +{p.bowlingPts}pts</span>
                            </span>
                          )}
                          {p.basePts > 0 && <span style={{ fontSize: 10, color: "#7d8590" }}>+{p.basePts} base</span>}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: p.isCaptain ? "#ffd166" : p.isViceCaptain ? "#7B68EE" : "#e6edf3" }}>{p.points}</div>
                      <div style={{ fontSize: 9, color: "#7d8590" }}>pts</div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {!breakdown && !loading && !error && item.players?.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "#7d8590", marginBottom: 10 }}>Your Squad (breakdown not yet available)</div>
              {item.players.map(name => (
                <div key={name} style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 8, padding: "8px 12px", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "#e6edf3" }}>{name}</span>
                  {name === item.captain && <span style={{ fontSize: 10, fontWeight: 700, color: "#ffd166" }}>C</span>}
                  {name === item.viceCaptain && <span style={{ fontSize: 10, fontWeight: 700, color: "#7B68EE" }}>VC</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailModal({ item, onClose }) {
  const statusColor = (s) => ({
    won: "#1D9E75", lost: "#E24B4A", draw: "#888780", refund: "#185FA5", refunded: "#185FA5",
    active: "#7F77DD", cancelled: "#888780", settled: "#3C3489",
  }[s] || "#BA7517");

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 420,
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid #e8e6dc",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#FAFAF7",
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#444441" }}>{item.typeEmoji} {item.typeLabel} Details</div>
            <div style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>{item.matchLabel}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "1px solid #d3d1c7", color: "#888780", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>×</button>
        </div>

        <div style={{ padding: "16px 20px" }}>
          <div style={{
            padding: "12px 16px", borderRadius: 10, marginBottom: 14,
            background: `${statusColor(item.status)}11`,
            border: `1px solid ${statusColor(item.status)}44`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: statusColor(item.status) }}>
              {item.status === "won" ? "🏆 Won!"
                : item.status === "lost" ? "😢 Lost"
                  : item.status === "draw" ? "🤝 Draw"
                    : item.status === "refund" || item.status === "refunded" ? "🔄 Refunded"
                      : item.status === "active" ? "⚡ Active"
                        : item.status === "settled" ? "✅ Settled"
                          : "⏳ Pending"}
            </span>
            <span style={{ fontSize: 12, color: "#888780" }}>{new Date(item.createdAt).toLocaleDateString()}</span>
          </div>

          {[
            item.type === "bet" && { label: "Team Picked", value: item.team },
            item.type === "bet" && { label: "Odds", value: `${item.odds}x` },
            item.type === "contest" && { label: "Contest Name", value: item.contestName },
            item.type === "contest" && { label: "Team Picked", value: item.team },
            item.type === "challenge" && { label: "Opponent", value: item.opponent || "—" },
            item.type === "challenge" && { label: "Team Picked", value: item.team },
            { label: "Amount", value: `${item.amount} pts` },
            item.status === "won" && item.type === "bet" && { label: "Winnings", value: `${Math.floor(item.amount * (item.odds || 2))} pts`, highlight: true },
            item.status === "won" && item.type === "contest" && { label: "Prize", value: `${item.prize} pts`, highlight: true },
            item.status === "won" && item.type === "challenge" && { label: "Prize", value: `${item.amount * 2} pts`, highlight: true },
            (item.status === "refund" || item.status === "refunded") && { label: "Refund", value: `${item.amount} pts returned`, highlight: true },
            item.detail && { label: "Info", value: item.detail },
          ].filter(Boolean).map((row, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f1efe8" }}>
              <span style={{ fontSize: 13, color: "#888780" }}>{row.label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: row.highlight ? "#1D9E75" : "#444441" }}>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── NEW: Points Progress Bar Component ──────────────────────────────────────
function PointsProgressCard({ points, lockedPoints, allHistory }) {
  const LEVEL_THRESHOLDS = [
    { level: 1, label: "Rookie", min: 0, max: 2000, color: "#888780" },
    { level: 2, label: "Bettor", min: 2000, max: 5000, color: "#3b82f6" },
    { level: 3, label: "Hustler", min: 5000, max: 10000, color: "#8b5cf6" },
    { level: 4, label: "Sharpie", min: 10000, max: 20000, color: "#f97316" },
    { level: 5, label: "Legend", min: 20000, max: 50000, color: "#eab308" },
    { level: 6, label: "Immortal", min: 50000, max: Infinity, color: "#e11d48" },
  ];

  const currentTier = LEVEL_THRESHOLDS.find(t => points >= t.min && points < t.max) || LEVEL_THRESHOLDS[0];
  const nextTier = LEVEL_THRESHOLDS[currentTier.level] || null;
  const progressPct = nextTier
    ? Math.min(((points - currentTier.min) / (nextTier.min - currentTier.min)) * 100, 100)
    : 100;

  const wins = allHistory.filter(b => b.status === "won").length;
  const losses = allHistory.filter(b => b.status === "lost").length;
  const winRate = allHistory.length > 0 ? Math.round((wins / allHistory.length) * 100) : 0;

  return (
    <div style={{
      background: "linear-gradient(135deg, rgba(244,196,48,0.07) 0%, rgba(30,29,43,0.9) 100%)",
      border: "1px solid rgba(244,196,48,0.2)",
      borderRadius: 16,
      padding: "20px 22px",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* decorative glow orb */}
      <div style={{
        position: "absolute", top: -30, right: -30, width: 120, height: 120,
        borderRadius: "50%", background: `${currentTier.color}22`, pointerEvents: "none",
      }} />

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: "#888", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 4 }}>Your Balance</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 32, fontWeight: 800, color: "#f4c430", lineHeight: 1 }}>
              {points.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>pts</span>
          </div>
          {lockedPoints > 0 && (
            <div style={{ fontSize: 12, color: "#BA7517", marginTop: 4 }}>
              🔒 {lockedPoints.toLocaleString()} locked in active bets
            </div>
          )}
        </div>
        <div style={{
          background: `${currentTier.color}22`,
          border: `1px solid ${currentTier.color}55`,
          borderRadius: 10,
          padding: "6px 14px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 10, color: currentTier.color, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>Level {currentTier.level}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: currentTier.color, marginTop: 1 }}>{currentTier.label}</div>
        </div>
      </div>

      {/* Progress bar */}
      {nextTier && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: "#666" }}>{currentTier.label}</span>
            <span style={{ fontSize: 11, color: "#666" }}>
              {(nextTier.min - points).toLocaleString()} pts to {nextTier.label}
            </span>
          </div>
          <div style={{ height: 8, background: "rgba(255,255,255,0.07)", borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${currentTier.color}99, ${currentTier.color})`,
              borderRadius: 99,
              transition: "width 1s cubic-bezier(0.34,1.56,0.64,1)",
            }} />
          </div>
        </div>
      )}

      {/* Mini stats row */}
      <div style={{ display: "flex", gap: 10 }}>
        {[
          { label: "Total Bets", value: allHistory.length, color: "#7F77DD" },
          { label: "Wins", value: wins, color: "#1D9E75" },
          { label: "Win Rate", value: `${winRate}%`, color: "#f4c430" },
          { label: "Pending", value: allHistory.filter(b => b.status === "pending" || b.status === "active").length, color: "#BA7517" },
        ].map(stat => (
          <div key={stat.label} style={{
            flex: 1,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 10,
            padding: "8px 10px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── NEW: Featured Match Banner ───────────────────────────────────────────────
function FeaturedMatchBanner({ onBetClick, onFantasyClick }) {
  return (
    <div style={{
      background: "linear-gradient(135deg, #0f2e1a 0%, #1a3a20 50%, #0d2616 100%)",
      border: "1px solid rgba(34,197,94,0.25)",
      borderRadius: 16,
      padding: "20px 22px",
      marginBottom: 20,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* background pattern dots */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: "radial-gradient(circle, #22c55e 1px, transparent 1px)",
        backgroundSize: "20px 20px",
        pointerEvents: "none",
      }} />
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)",
        borderRadius: 99, padding: "3px 10px", marginBottom: 12,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", boxShadow: "0 0 6px #22c55e" }} />
        <span style={{ fontSize: 10, color: "#22c55e", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>Featured Match</span>
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 4 }}>🏏 IPL 2025 Season</div>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>Live matches • Fantasy 11 • Contests</div>
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onBetClick}
          style={{
            flex: 1, padding: "10px 0", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "#fff",
            fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          🎯 Bet Now
        </button>
        <button
          onClick={onFantasyClick}
          style={{
            flex: 1, padding: "10px 0", borderRadius: 10,
            border: "1px solid rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)",
            color: "#22c55e", fontWeight: 700, fontSize: 13, cursor: "pointer",
          }}
        >
          🏆 Fantasy 11
        </button>
      </div>
    </div>
  );
}

// ── NEW: Recent Activity Feed ────────────────────────────────────────────────
function RecentActivityFeed({ allHistory }) {
  const recent = allHistory.slice(0, 5);

  const statusColor = (s) => ({
    won: "#1D9E75", lost: "#E24B4A", draw: "#888780",
    refund: "#185FA5", refunded: "#185FA5",
    active: "#7F77DD", cancelled: "#888780", settled: "#3C3489",
  }[s] || "#BA7517");

  const statusEmoji = (s) => ({
    won: "🏆", lost: "😢", draw: "🤝",
    refund: "🔄", refunded: "🔄",
    active: "⚡", cancelled: "❌", settled: "✅",
  }[s] || "⏳");

  if (recent.length === 0) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Recent Activity
        </div>
        <div style={{ fontSize: 11, color: "#555" }}>Last {recent.length} entries</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {recent.map((item) => (
          <div key={`${item.type}-${item._id}`} style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderLeft: `3px solid ${statusColor(item.status)}`,
            borderRadius: 10,
            padding: "10px 14px",
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{statusEmoji(item.status)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.matchLabel}
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                {item.typeEmoji} {item.typeLabel}
                {item.type !== "fantasy11" && item.team && item.team !== "—" && ` · ${item.team}`}
              </div>
            </div>
            <div style={{
              fontSize: 12, fontWeight: 700, color: statusColor(item.status),
              background: `${statusColor(item.status)}18`,
              padding: "3px 10px", borderRadius: 99, flexShrink: 0,
              border: `1px solid ${statusColor(item.status)}33`,
            }}>
              {item.status.toUpperCase()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const { toasts, remove: removeToast, toast } = useToast();
  const { floaters, fire: fireFloat } = useFloatingPoints();

const [screen, setScreenState] = useState(() => {
    const hash = window.location.hash.replace(/^#\/?/, "");
    return hash || "auth";
  });

  const setScreen = useCallback((newScreen) => {
    window.location.hash = "/" + newScreen;
    setScreenState(newScreen);
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace(/^#\/?/, "");
      if (hash) setScreenState(hash);
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);
  const [authMode, setAuthMode] = useState("login");
  const [username, setUsername] = useState("");
  const [inputName, setInputName] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [points, setPoints] = useState(1000);
  const [lockedPoints, setLockedPoints] = useState(0);
  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [systemLocked, setSystemLocked] = useState(false);

  useEffect(() => {
    async function checkLockout() {
      try {
        const res = await fetch(`${API}/admin/system-status`);
        const data = await res.json();
        if (data && data.systemLocked) {
          setSystemLocked(true);
        } else {
          setSystemLocked(false);
        }
      } catch (err) {
        console.error("Lockout status check failed:", err);
      }
    }
    checkLockout();
    const timer = setInterval(checkLockout, 10000);
    return () => clearInterval(timer);
  }, []);
  const [betAmount, setBetAmount] = useState("");
  const [betPlaced, setBetPlaced] = useState(null);
  const [userTheme, setUserTheme] = useState("spiderverse");
  const [myBets, setMyBets] = useState([]);
  const [allHistory, setAllHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [animatePoints, setAnimatePoints] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [prefilledMatch, setPrefilledMatch] = useState(null);
  const [matchStatus, setMatchStatus] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Forgot Password States
  const [forgotPasswordStep, setForgotPasswordStep] = useState(false);
  const [forgotPasswordUsername, setForgotPasswordUsername] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOtpSent, setForgotPasswordOtpSent] = useState(false);
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState("");
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState("");
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState("");

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#/secret-admin-portal") {
        setScreen("admin");
      } else if (window.location.hash === "#/cw-live") {
        // Clear the hash so it doesn't stay in the URL
        window.history.replaceState(null, "", window.location.pathname);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("fb-theme") || "dark";
    document.documentElement.setAttribute("data-theme", saved);
  }, []);

  const [homeLoaded, setHomeLoaded] = useState(false);
  const displayPoints = useCountUp(points, 1400, screen === "home" && !homeLoaded);

  useEffect(() => {
    if (screen === "home" && !homeLoaded) {
      const t = setTimeout(() => setHomeLoaded(true), 1600);
      return () => clearTimeout(t);
    }
  }, [screen]);

  const resetHome = () => setHomeLoaded(false);

  const [globalLiveCount, setGlobalLiveCount] = useState(0);
  const prevScreenRef = useRef(screen);
  useEffect(() => {
    const prev = prevScreenRef.current;
    prevScreenRef.current = screen;
    // Only push a real history entry when moving from home into a sub-screen
    if (prev === "home" && screen !== "home" && screen !== "auth") {
      window.history.pushState({ screen }, "", window.location.pathname);
    } else {
      window.history.replaceState({ screen }, "", window.location.pathname);
    }
  }, [screen]);
  useEffect(() => {
    const handleBack = (e) => {
      if (e.state?.screen) {
        setScreen(e.state.screen);
      } else if (username) {
        setScreen("home");
      }
    };
    window.addEventListener("popstate", handleBack);
    return () => window.removeEventListener("popstate", handleBack);
  }, [username]);

  useEffect(() => {
    if (!username) return;
    async function fetchLiveCount() {
      try {
        const res = await fetch(`${API}/ipl-matches`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.matches || [];
        const live = list.filter(m => m.status === "live").length;
        setGlobalLiveCount(live);
      } catch (err) {
        console.error("Failed to fetch live count", err);
      }
    }
    fetchLiveCount();
    const interval = setInterval(fetchLiveCount, 30000);
    return () => clearInterval(interval);
  }, [username]);



  useEffect(() => {
    if (username) { fetchMyBets(); fetchAllHistory(); }
  }, [username]);

  useEffect(() => {
    if (!prefilledMatch) { setMatchStatus(null); return; }

    async function checkMatchStatus() {
      try {
        const isFootball = prefilledMatch.matchId?.startsWith("football-") ||
          prefilledMatch.matchId?.startsWith("epl-") ||
          prefilledMatch.matchId?.startsWith("ucl-");

        const endpoint = isFootball ? `${API}/football-matches` : `${API}/ipl-matches`;
        const res = await fetch(endpoint);
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.matches || []);
        const found = list.find(m => m.id === prefilledMatch.matchId);
        if (found) setMatchStatus(found.status);
      } catch (err) {
        console.error("Failed to check match status", err);
      }
    }
    checkMatchStatus();
    const interval = setInterval(checkMatchStatus, 60000);
    return () => clearInterval(interval);
  }, [prefilledMatch]);

  const fetchMyBets = async () => {
    try {
      const res = await fetch(`${API}/bets/${username}`);
      const data = await res.json();
      if (res.ok) setMyBets(data);
    } catch (err) { console.error("Failed to fetch bets", err); }
  };

  const fetchAllHistory = async () => {
    try {
      const [betsRes, contestsRes, challengesRes, fantasy11Res] = await Promise.all([
        fetch(`${API}/bets/${username}`),
        fetch(`${API}/contests/${username}`),
        fetch(`${API}/challenges/${username}`),
        fetch(`${API}/fantasy11/my-teams/${username}`),
      ]);

      const betsData = betsRes.ok ? await betsRes.json() : [];
      const contestsData = contestsRes.ok ? await contestsRes.json() : { contests: [] };
      const challengesData = challengesRes.ok ? await challengesRes.json() : { challenges: [] };
      const fantasy11Data = fantasy11Res.ok ? await fantasy11Res.json() : { teams: [] };

      const bets = Array.isArray(betsData) ? betsData : [];
      const contests = contestsData.contests || [];
      const challenges = challengesData.challenges || [];
      const f11Teams = fantasy11Data.teams || [];

      const normalizedBets = bets.map(b => ({
        _id: b._id, type: "bet", typeLabel: "Solo Bet", typeEmoji: "🎯",
        matchLabel: b.matchLabel, team: b.team, amount: b.amount,
        odds: b.odds || 2.0, status: b.status, createdAt: b.createdAt, detail: null,
      }));

      const normalizedContests = contests.map(c => {
        const myEntry = c.participants?.find(p => p.username === username);
        const totalPot = c.entryFee * (c.participants?.length || 1);
        const winnerStr = c.winner || "";
        const winners = winnerStr ? winnerStr.split(", ").filter(Boolean) : [];
        const isRefund = winnerStr === "refund" || winnerStr === "no_scores" || winnerStr === "";
        const isSolo = (c.participants?.length || 0) === 1 && c.participants?.[0]?.username === username;
        let status = "pending";
        if (c.status === "settled") {
          if (isRefund || isSolo) status = "refund";
          else if (winners.includes(username)) status = "won";
          else status = "lost";
        }
        if (c.status === "cancelled") status = "cancelled";
        const winnerCount = winners.length || 1;
        const prize = status === "won" ? Math.floor(totalPot / winnerCount) : 0;
        return {
          _id: c._id, type: "contest", typeLabel: "Contest", typeEmoji: "🏆",
          matchLabel: c.matchLabel, team: myEntry?.team || "—",
          amount: c.entryFee, prize, totalPot, status,
          contestStatus: c.status, winningTeam: c.winningTeam,
          contestName: c.name, createdAt: c.createdAt,
          matchId: c.matchId || null,
          detail: `${c.participants?.length || 1}/${c.maxPlayers} players`,
        };
      });

      const normalizedChallenges = challenges
        .filter(c => c.status !== "cancelled")
        .map(c => {
          const isChallenger = c.challenger === username;
          const myTeam = isChallenger ? c.challengerTeam : c.opponentTeam;
          let status = "pending";
          if (c.status === "settled") {
            if (c.winner === "draw") status = "draw";
            else status = c.winner === username ? "won" : "lost";
          }
          if (c.status === "active") status = "active";
          return {
            _id: c._id, type: "challenge", typeLabel: "Challenge", typeEmoji: "⚔️",
            matchLabel: c.matchLabel, team: myTeam || "—", amount: c.wager,
            prize: c.status === "settled" && c.winner === username ? c.wager * 2 : 0,
            status, opponent: isChallenger ? c.opponent : c.challenger,
            createdAt: c.createdAt,
            detail: isChallenger ? `You challenged ${c.opponent || "invited players"}` : `Challenged by ${c.challenger}`,
          };
        });

      const normalizedFantasy11 = f11Teams.map(t => {
        const hasResult = t.fantasyPoints !== null && t.fantasyPoints !== undefined;
        return {
          _id: t._id, type: "fantasy11", typeLabel: "Fantasy 11", typeEmoji: "🏏",
          matchLabel: t.matchLabel || t.matchId,
          team: `C: ${t.captain || "—"} · VC: ${t.viceCaptain || "—"}`,
          amount: 0, fantasyPoints: t.fantasyPoints,
          status: hasResult ? "settled" : t.locked ? "active" : "pending",
          createdAt: t.createdAt,
          detail: hasResult ? `${t.fantasyPoints} fantasy pts scored` : t.locked ? "Match in progress" : "Squad saved — awaiting match",
          players: t.players || [], captain: t.captain, viceCaptain: t.viceCaptain, matchId: t.matchId,
        };
      });

      const merged = [
        ...normalizedBets,
        ...normalizedContests,
        ...normalizedChallenges,
        ...normalizedFantasy11,
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setAllHistory(merged);
    } catch (err) { console.error("Failed to fetch history", err); }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API}/leaderboard`);
      const data = await res.json();
      if (res.ok) setLeaderboard(data.users);
    } catch (err) { console.error("Failed to fetch leaderboard", err); }
  };
  const handleAuth = async () => {
    if (!inputName.trim() || !inputPassword.trim()) {
      setError("Please enter both username and password!");
      return;
    }

    // ── Login ────────────────────────────────────────────────────────────────
    if (authMode === "login") {
      setLoading(true);
      setError("");
      try {
        let email = inputName.trim();

        // If it doesn't look like an email, lookup email by username first
        if (!email.includes("@")) {
          const emailRes = await fetch(`${API}/auth/get-email`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: email }),
          });
          const emailData = await emailRes.json();
          if (!emailRes.ok) {
            setError(emailData.message || "Username not found!");
            setLoading(false);
            return;
          }
          email = emailData.email;
        }

        if (!email || !email.trim()) {
          setError("This account does not have an email address associated with it. Please login using your email directly or create a new account.");
          setLoading(false);
          return;
        }

        // 1. Sign in to Firebase Auth
        const userCredential = await signInWithEmailAndPassword(auth, email, inputPassword);
        const idToken = await userCredential.user.getIdToken();

        // 2. Fetch/Verify user points and info from backend
        const res = await fetch(`${API}/auth/firebase-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        const data = await res.json();
        if (res.ok) {
          setUsername(data.name);
          setPoints(data.points);
          setLockedPoints(data.lockedPoints || 0);
          toast.success("Welcome back!", `Logged in as ${data.name}`, 3000);
          setScreen("home");
          fetchLeaderboard();
        } else {
          setError(data.message || "Failed to retrieve user data!");
          toast.error("Login Failed", data.message);
        }
      } catch (err) {
        console.error(err);
        let errMsg = "Can't connect to server!";
        if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
          errMsg = "Wrong password or invalid credentials!";
        } else if (err.message) {
          errMsg = err.message;
        }
        setError(errMsg);
      }
      setLoading(false);
      return;
    }

    // ── Registration ──────────────────────────────────────────────────────────
    if (!pendingEmail.trim()) {
      setError("Please enter your email!");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // 1. Verify username availability on backend
      const checkUserRes = await fetch(`${API}/auth/check-username`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: inputName.trim() }),
      });
      const checkUserData = await checkUserRes.json();
      if (!checkUserRes.ok) {
        setError(checkUserData.message || "Username is already taken!");
        setLoading(false);
        return;
      }

      // 2. Verify email availability on backend
      const checkEmailRes = await fetch(`${API}/auth/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail.trim() }),
      });
      const checkEmailData = await checkEmailRes.json();
      if (!checkEmailRes.ok) {
        setError(checkEmailData.message || "Email is already registered!");
        setLoading(false);
        return;
      }

      // 3. Register user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, pendingEmail.trim(), inputPassword);
      const idToken = await userCredential.user.getIdToken();

      // 4. Register/Sync in MongoDB
      const res = await fetch(`${API}/auth/firebase-register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken, name: inputName.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setUsername(data.name);
        setPoints(data.points);
        setLockedPoints(data.lockedPoints || 0);
        toast.success("Account created!", `Welcome ${data.name} · 1000 pts`, 3000);
        setScreen("home");
        fetchLeaderboard();
      } else {
        setError(data.message || "Failed to register account with database.");
      }
    } catch (err) {
      console.error(err);
      let errMsg = "Can't connect to server!";
      if (err.code === "auth/email-already-in-use") {
        errMsg = "Email already in use on Firebase!";
      } else if (err.code === "auth/weak-password") {
        errMsg = "Password should be at least 6 characters!";
      } else if (err.code === "auth/invalid-email") {
        errMsg = "Invalid email format!";
      } else if (err.message) {
        errMsg = err.message;
      }
      setError(errMsg);
    }
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      const res = await fetch(`${API}/auth/firebase-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = await res.json();
      if (res.ok) {
        setUsername(data.name);
        setPoints(data.points);
        setLockedPoints(data.lockedPoints || 0);
        toast.success("Welcome back!", `Logged in with Google as ${data.name}`, 3000);
        setScreen("home");
        fetchLeaderboard();
      } else {
        setError(data.message || "Failed to retrieve user data!");
        toast.error("Login Failed", data.message);
      }
    } catch (err) {
      console.error(err);
      let errMsg = "Google Login failed.";
      if (err.message) errMsg = err.message;
      setError(errMsg);
      toast.error("Auth Error", errMsg);
    }
    setLoading(false);
  };

  const handleForgotPasswordSendOtp = async () => {
    if (!forgotPasswordUsername.trim() || !forgotPasswordEmail.trim()) {
      setError("Both username and email are required!");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/forgot-password/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: forgotPasswordUsername.trim(),
          email: forgotPasswordEmail.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setForgotPasswordOtpSent(true);
        toast.success("OTP Sent!", "Check your email for the 6-digit reset code 📧", 4000);
      } else {
        setError(data.message || "Failed to send reset OTP");
      }
    } catch (err) {
      console.error(err);
      setError("Can't connect to server!");
    }
    setLoading(false);
  };

  const handleForgotPasswordReset = async () => {
    if (!forgotPasswordOtp.trim() || !forgotPasswordNewPassword.trim() || !forgotPasswordConfirmPassword.trim()) {
      setError("All fields are required!");
      return;
    }
    if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    if (forgotPasswordNewPassword.length < 6) {
      setError("Password must be at least 6 characters long!");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/forgot-password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: forgotPasswordEmail.trim(),
          otp: forgotPasswordOtp.trim(),
          newPassword: forgotPasswordNewPassword.trim(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Password Reset Successfully!", "You can now log in with your new password.", 4000);
        setForgotPasswordStep(false);
        setForgotPasswordOtpSent(false);
        setForgotPasswordUsername("");
        setForgotPasswordEmail("");
        setForgotPasswordOtp("");
        setForgotPasswordNewPassword("");
        setForgotPasswordConfirmPassword("");
        setError("");
      } else {
        setError(data.message || "Failed to reset password");
      }
    } catch (err) {
      console.error(err);
      setError("Can't connect to server!");
    }
    setLoading(false);
  };


  const handleBetOnMatch = (matchInfo) => {
    const iplSport = { id: "cricket", name: "Cricket", emoji: "🏏", teams: matchInfo.teams };
    setSelectedSport(iplSport); setSelectedTeam(null); setBetAmount("");
    setPrefilledMatch(matchInfo); setMatchStatus(matchInfo.status || null); setError("");
    setScreen("bet");
  };

  const handleFantasy11 = (matchInfo) => {
    setPrefilledMatch(matchInfo);
    setMatchStatus(matchInfo.status || null);
    setScreen("fantasy11");
  };

  const placeBet = async () => {
    // ── Match status guards ──────────────────────────────────────────────────
    if (matchStatus === "live") {
      setError("Predicting is closed — this match has already started!");
      toast.warning("Predicting Locked", "This match has already started.");
      return;
    }
    if (matchStatus === "completed") {
      setError("Predicting is closed — this match has already ended!");
      toast.warning("Predicting Locked", "This match has already ended.");
      return;
    }

    // ── Input validation ─────────────────────────────────────────────────────
    const amount = parseInt(betAmount);
    if (!amount || amount <= 0 || amount > points) return;

    if (!selectedTeam) {
      setError("Please pick a team!");
      toast.error("Pick a Team", "Select a team before placing your bet.");
      return;
    }

    if (!prefilledMatch) {
      setError("Please go to 🏏 IPL or ⚽ Football tab and click Bet Now on a match first!");
      return;
    }

    const isFootball = prefilledMatch.matchId?.startsWith("football-") ||
      prefilledMatch.matchId?.startsWith("epl-") ||
      prefilledMatch.matchId?.startsWith("ucl-");

    // ── Place bet ────────────────────────────────────────────────────────────
    setLoading(true);
    setError("");

    try {
      const teamOdds = prefilledMatch.odds?.[selectedTeam] || 2.0;

      const res = await fetch(`${API}/bet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: username,
          amount,
          matchId: String(prefilledMatch.matchId),
          matchLabel: prefilledMatch.matchLabel,
          team: selectedTeam,
          odds: teamOdds,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        setAnimatePoints(true);
        setTimeout(() => setAnimatePoints(false), 1000);
        setPoints(data.points);
        setLockedPoints(data.lockedPoints);
        fireFloat(-amount);
        toast.bet(`${amount} pts on ${selectedTeam} · ${prefilledMatch.matchLabel}`, 4000);
        setBetPlaced({
          themeId: userTheme,
          amount,
          team: selectedTeam,
          matchLabel: prefilledMatch.matchLabel,
          odds: teamOdds,
          potentialWin: Math.floor(amount * teamOdds),
        });
        fireConfetti();
        fetchMyBets();
        fetchAllHistory();
        fetchLeaderboard();
      } else {
        setError(data.message || "Bet failed!");
        toast.error("Bet Failed", data.message || "Something went wrong. Try again.");
      }
    } catch (err) {
      setError("Can't connect to server!");
      toast.error("Connection Error", "Can't reach the server. Try again.");
    }

    setLoading(false);
  }; // ← end of placeBet

  // ── These must all be OUTSIDE placeBet ──────────────────────────────────────

  const handleLogout = () => {
    setUsername(""); setInputName(""); setInputPassword("");
    setPoints(1000); setLockedPoints(0);
    setMyBets([]); setAllHistory([]); setLeaderboard([]);
    setScreen("auth"); setAuthMode("login");
    setPrefilledMatch(null); setMatchStatus(null);
    toast.info("Logged out", "See you next time! 👋", 2500);
    resetHome();
  };

  const statusColor = (s) => ({
    won: "#1D9E75",
    lost: "#E24B4A",
    draw: "#888780",
    refund: "#185FA5",
    refunded: "#185FA5",
    active: "#7F77DD",
    cancelled: "#888780",
    settled: "#3C3489",
  }[s] || "#BA7517");

  const statusEmoji = (s) => ({
    won: "🏆",
    lost: "😢",
    draw: "🤝",
    refund: "🔄",
    refunded: "🔄",
    active: "⚡",
    cancelled: "❌",
    settled: "✅",
  }[s] || "⏳");

  const statusLabel = (s) => ({
    won: "WON",
    lost: "LOST",
    draw: "DRAW",
    refund: "REFUNDED",
    refunded: "REFUNDED",
    active: "ACTIVE",
    cancelled: "CANCELLED",
    settled: "SETTLED",
  }[s] || "PENDING");

  const pointsDisplay = (item) => {
    if (item.type === "fantasy11") {
      if (item.fantasyPoints !== null && item.fantasyPoints !== undefined)
        return `${item.fantasyPoints} pts scored`;
      return item.locked ? "⚡ In progress" : "⏳ Pending";
    }
    if (item.status === "won") {
      if (item.type === "bet") return `+${Math.floor(item.amount * (item.odds || 2.0)) - item.amount} pts profit`;
      if (item.type === "contest") return `+${item.prize - item.amount} pts profit`;
      if (item.type === "challenge") return `+${item.amount} pts profit`;
    }
    if (item.status === "lost") return `-${item.amount} pts`;
    if (item.status === "draw") return "refunded";
    if (item.status === "refund" || item.status === "refunded") return "↩️ refunded";
    if (item.status === "cancelled") return "refunded";
    return `🔒 ${item.amount} pts`;
  };

  const leaderboardList = () => {
    const others = leaderboard.filter(p => p.name !== username);
    const me = { name: username + " (You)", points };
    return [...others, me].sort((a, b) => b.points - a.points);
  };

  const maxPoints = Math.max(...leaderboardList().map(p => p.points), 1000);
  const pendingCount = myBets.filter(b => b.status === "pending").length;
  const isBettingLocked = matchStatus === "completed";
  const isLiveInPlay = matchStatus === "live";

  const historyTabs = [
    { key: "all", label: "All", emoji: "", color: "#7F77DD" },
    { key: "bet", label: "Solo Bets", emoji: "🎯", color: "#BA7517" },
    { key: "contest", label: "Contests", emoji: "🏆", color: "#1D9E75" },
    { key: "challenge", label: "Challenges", emoji: "⚔️", color: "#E24B4A" },
    { key: "fantasy11", label: "Fantasy 11", emoji: "🏏", color: "#ffd166" },
  ];

  const filteredHistory = historyFilter === "all"
    ? allHistory
    : allHistory.filter(h => h.type === historyFilter);

  if (systemLocked) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle at center, #1a0f0f 0%, #0d0606 100%)",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: 20,
        textAlign: "center"
      }}>
        <div style={{
          background: "rgba(255, 255, 255, 0.02)",
          border: "1px solid rgba(239, 68, 68, 0.2)",
          borderRadius: 24,
          padding: "48px 32px",
          maxWidth: 480,
          width: "100%",
          boxShadow: "0 20px 50px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.05)",
          backdropFilter: "blur(20px)"
        }}>
          <div style={{
            fontSize: 64,
            marginBottom: 20
          }}>🚨</div>
          <h1 style={{
            fontSize: 28,
            fontWeight: 800,
            color: "#f87171",
            margin: "0 0 12px",
            letterSpacing: "-0.5px"
          }}>SYSTEM SUSPENDED</h1>
          <p style={{
            fontSize: 14,
            color: "#a1a1aa",
            lineHeight: 1.6,
            margin: "0 0 32px"
          }}>
            CrickWager is undergoing critical infrastructure security maintenance. All gaming channels, wagers, and profiles have been suspended by security operations.
          </p>
          <div style={{
            fontSize: 12,
            color: "#ef4444",
            background: "rgba(239, 68, 68, 0.1)",
            padding: "10px 16px",
            borderRadius: 8,
            border: "1px solid rgba(239, 68, 68, 0.2)",
            fontFamily: "monospace",
            display: "inline-block"
          }}>
            CRITICAL_SAFETY_LOCKOUT_ACTIVE
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* ── Global animation keyframes ── */}
      <style>{`
          @keyframes floatUp {
            0%   { transform: translateY(0) scale(1);   opacity: 1; }
            60%  { transform: translateY(-52px) scale(1.1); opacity: 1; }
            100% { transform: translateY(-80px) scale(0.9); opacity: 0; }
          }
          @keyframes toastProgress {
            from { width: 100%; }
            to   { width: 0%; }
          }
          @keyframes pulseRed {
            0% { box-shadow: 0 0 0 0 rgba(239, 1, 7, 0.7); }
            70% { box-shadow: 0 0 0 6px rgba(239, 1, 7, 0); }
            100% { box-shadow: 0 0 0 0 rgba(239, 1, 7, 0); }
          }
          .nav-live-badge {
            background: #EF0107;
            color: #fff;
            border-radius: 99px;
            padding: 1px 6px;
            font-size: 9px;
            font-weight: 700;
            margin-left: 5px;
            letter-spacing: 0.04em;
            display: inline-flex;
            align-items: center;
            animation: pulseRed 1.5s infinite;
          }
          .bottom-nav-live-dot {
            position: absolute;
            top: 2px;
            right: calc(50% - 14px);
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: #EF0107;
            border: 1px solid #000;
            animation: blink 1.2s infinite;
          }
        `}</style>

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* ── Floating points ── */}
      <FloatingPoints floaters={floaters} />

      <div className="bg-grid" />
      <div className="bg-glow" />

      {/* ── Modals ── */}
      {selectedHistoryItem && (selectedHistoryItem.type === "fantasy11" || selectedHistoryItem.team === "fantasy11") && (
        <Fantasy11BreakdownModal
          item={{
            ...selectedHistoryItem,
            matchId: selectedHistoryItem.matchId
              ? ("" + selectedHistoryItem.matchId).replace(/^ipl-?(\d+)/, "ipl-$1")
              : null,
          }}
          username={username}
          onClose={() => setSelectedHistoryItem(null)}
        />
      )}
      {selectedHistoryItem && selectedHistoryItem.type !== "fantasy11" && selectedHistoryItem.team !== "fantasy11" && (
        <DetailModal item={selectedHistoryItem} onClose={() => setSelectedHistoryItem(null)} />
      )}

      {/* ── AUTH ── */}
      {screen === "auth" && (
        <div className="login-screen">
          <div className="login-hero">
            <div className="hero-content">
              <div className="hero-logo">⚡ CRICKWAGER</div>
              <h1 className="hero-title">The Ultimate Arena for Premium Sports Gaming.</h1>
              <p className="hero-subtitle">
                Join thousands of players in structured room lobbies, action-packed fantasy sports, and real-time multiplayer contests.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <span className="feature-icon">🏏</span>
                  <span className="feature-text">Live IPL Gaming</span>
                </div>
                <div className="hero-feature">
                  <span className="feature-icon">⚽</span>
                  <span className="feature-text">Global Football</span>
                </div>
                <div className="hero-feature">
                  <span className="feature-icon">🏆</span>
                  <span className="feature-text">Fantasy 11 Squads</span>
                </div>
                <div className="hero-feature">
                  <span className="feature-icon">⚔️</span>
                  <span className="feature-text">Multiplayer Clashes</span>
                </div>
              </div>
            </div>
            <div className="hero-overlay"></div>
          </div>

          <div className="login-form-container">
            <div className="login-box">
              <div className="login-logo mobile-only">⚡ CRICKWAGER</div>

              {forgotPasswordStep ? (
                <>
                  <h1 className="login-title">Reset Password</h1>
                  <p className="login-sub">Recover access to your CrickWager account</p>

                  {!forgotPasswordOtpSent ? (
                    <>
                      <input className="login-input" placeholder="Username" type="text" value={forgotPasswordUsername}
                        onChange={e => setForgotPasswordUsername(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgotPasswordSendOtp()} />
                      <input className="login-input" placeholder="Registered Email" type="email" value={forgotPasswordEmail}
                        onChange={e => setForgotPasswordEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgotPasswordSendOtp()} />

                      {error && <div className="error-msg">{error}</div>}
                      <button className="btn-primary" onClick={handleForgotPasswordSendOtp} disabled={loading}>
                        {loading ? <LoadingDots /> : "Send Reset OTP →"}
                      </button>
                    </>
                  ) : (
                    <>
                      <div style={{ textAlign: "center", color: "#22c55e", fontSize: 13, marginBottom: 12, lineHeight: "1.4" }}>
                        📧 OTP sent to your registered email! Please check <strong style={{ color: "#fbbf24" }}>{forgotPasswordEmail}</strong>.
                      </div>
                      <input className="login-input" placeholder="6-Digit Reset OTP" type="text" value={forgotPasswordOtp}
                        onChange={e => setForgotPasswordOtp(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgotPasswordReset()} />
                      <input className="login-input" placeholder="New Password (min 6 chars)" type="password" value={forgotPasswordNewPassword}
                        onChange={e => setForgotPasswordNewPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgotPasswordReset()} />
                      <input className="login-input" placeholder="Confirm New Password" type="password" value={forgotPasswordConfirmPassword}
                        onChange={e => setForgotPasswordConfirmPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleForgotPasswordReset()} />

                      {error && <div className="error-msg">{error}</div>}
                      <button className="btn-primary" onClick={handleForgotPasswordReset} disabled={loading}>
                        {loading ? <LoadingDots /> : "Reset Password 🔒"}
                      </button>
                    </>
                  )}

                  <div style={{ textAlign: "center", marginTop: "16px" }}>
                    <button className="forgot-pwd-link" onClick={() => { setForgotPasswordStep(false); setForgotPasswordOtpSent(false); setError(""); }} style={{ background: "none", border: "none", color: "#9ca3af", textDecoration: "underline", cursor: "pointer", fontSize: 13 }}>
                      Back to Login
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="login-title">{authMode === "login" ? "Welcome Back" : "Join the Arena"}</h1>
                  <p className="login-sub">{authMode === "login" ? "Login to continue your journey" : "Register to get 1000 free points"}</p>
                  <div className="auth-tabs">
                    <button className={authMode === "login" ? "active" : ""} onClick={() => { setAuthMode("login"); setError(""); }}>Login</button>
                    <button className={authMode === "register" ? "active" : ""} onClick={() => { setAuthMode("register"); setError(""); }}>Register</button>
                  </div>
                  <input className="login-input" placeholder="Username" value={inputName}
                    onChange={e => setInputName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />

                  {authMode === "register" && (
                    <input className="login-input" placeholder="Email" type="email" value={pendingEmail}
                      onChange={e => setPendingEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />
                  )}

                  <input className="login-input" type="password" placeholder="Password" value={inputPassword}
                    onChange={e => setInputPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />

                  {error && <div className="error-msg">{error}</div>}
                  <button className="btn-primary" onClick={handleAuth} disabled={loading}>
                    {loading ? <LoadingDots /> : authMode === "login" ? "Login →" : "Create Account →"}
                  </button>

                  <div style={{ display: "flex", alignItems: "center", margin: "16px 0", color: "rgba(255,255,255,0.2)" }}>
                    <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }}></div>
                    <span style={{ fontSize: 11, padding: "0 10px", textTransform: "uppercase", letterSpacing: "0.05em", color: "rgba(255,255,255,0.4)" }}>or connect with</span>
                    <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }}></div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    style={{
                      width: "100%",
                      padding: "11px 16px",
                      borderRadius: "8px",
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "#fff",
                      fontSize: "13px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" />
                    </svg>
                    Continue with Google
                  </button>

                  {authMode === "login" && (
                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                      <button className="forgot-pwd-link" onClick={() => { setForgotPasswordStep(true); setError(""); }} style={{ background: "none", border: "none", color: "#9ca3af", textDecoration: "underline", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                        Forgot Password?
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN APP ── */}
      {screen !== "auth" && (
        <>
          {/* ── DESKTOP NAVIGATION ── */}
          {screen !== "admin" && (
            <nav className="nav">
              <div className="nav-logo" onClick={() => setScreen("home")} style={{ cursor: "pointer" }}>⚡ CRICKWAGER</div>
              <div className="nav-links">
                <button className={screen === "home" ? "active" : ""} onClick={() => setScreen("home")}>Home</button>
                <button className={screen === "matches" ? "active" : ""} onClick={() => setScreen("matches")}>🏏 IPL {globalLiveCount > 0 && <span className="nav-live-badge">🔴 LIVE ({globalLiveCount})</span>}</button>
                <button className={screen === "football" ? "active" : ""} onClick={() => setScreen("football")}>⚽ Football</button>
                <button className={screen === "fantasy11" ? "active" : ""} onClick={() => setScreen("fantasy11")}>🏆 Fantasy 11</button>
                <button className={screen === "leaderboard" ? "active" : ""} onClick={() => { fetchLeaderboard(); setScreen("leaderboard"); }}>Leaderboard</button>
                <button className={screen === "profile" ? "active" : ""} onClick={() => setScreen("profile")}>👤 Profile</button>
                <button className={screen === "history" ? "active" : ""} onClick={() => { fetchAllHistory(); setScreen("history"); }}>
                  History {pendingCount > 0 && (
                    <span style={{ background: "#BA7517", color: "#fff", borderRadius: 99, padding: "1px 6px", fontSize: 10, marginLeft: 4 }}>{pendingCount}</span>
                  )}
                </button>
                <button className={screen === "multiplayer" ? "active" : ""} onClick={() => setScreen("multiplayer")}>⚔️ Multiplayer</button>
                <button className={screen === "mines" ? "active" : ""} onClick={() => setScreen("mines")}>💣 Mines</button>
                <button className={screen === "shop" ? "active" : ""} onClick={() => setScreen("shop")}>💎 Shop</button>
                <button className={screen === "odds-api" ? "active" : ""} onClick={() => setScreen("odds-api")}>⚙️ Odds API</button>
                {username === "admin" && (
                  <button className={screen === "admin" ? "active" : ""} onClick={() => setScreen("admin")}>🛡️ Admin</button>
                )}
              </div>
              <div className="nav-right">
                <div className={`nav-points ${animatePoints ? "pulse" : ""}`}>
                  <span>💰</span>
                  <span className="points-value">{points.toLocaleString()}</span>
                  <span className="points-label">pts</span>
                </div>
                {lockedPoints > 0 && (
                  <div className="nav-points" style={{ borderColor: "#BA7517", opacity: 0.8 }}>
                    <span>🔒</span>
                    <span className="points-value" style={{ color: "#BA7517" }}>{lockedPoints.toLocaleString()}</span>
                    <span className="points-label">locked</span>
                  </div>
                )}
                <ThemeSwitcher />
                <button className="logout-btn" onClick={handleLogout}>Logout</button>
              </div>
            </nav>
          )}

          {/* ── MOBILE TOP BAR ── */}
          {screen !== "admin" && (
            <div className="mobile-top-bar">
              <div className="mobile-logo" onClick={() => setScreen("home")}>⚡ CRICKWAGER</div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div className="mobile-points">
                  <span>💰</span>
                  <span className="points-value">{points.toLocaleString()}</span>
                </div>
                <button className={`hamburger-btn ${mobileMenuOpen ? "open" : ""}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </button>
              </div>
            </div>
          )}

          {/* ── MOBILE HAMBURGER MENU DRAWER ── */}
          {screen !== "admin" && (
            <div className={`mobile-menu-drawer ${mobileMenuOpen ? "open" : ""}`}>
              <div className="drawer-backdrop" onClick={() => setMobileMenuOpen(false)}></div>
              <div className="drawer-content">
                <div className="drawer-header">
                  <div className="drawer-logo">⚡ CRICKWAGER</div>
                  <button className="drawer-close" onClick={() => setMobileMenuOpen(false)}>✕</button>
                </div>

                <div className="drawer-links">
                  <button className={`drawer-link-btn ${screen === "home" ? "active" : ""}`} onClick={() => { setScreen("home"); setMobileMenuOpen(false); }}>🏠 Home</button>
                  <button className={`drawer-link-btn ${screen === "matches" ? "active" : ""}`} onClick={() => { setScreen("matches"); setMobileMenuOpen(false); }}>
                    🏏 IPL Matches {globalLiveCount > 0 && <span style={{ background: "#EF0107", color: "#fff", borderRadius: 99, padding: "2px 8px", fontSize: 10, fontWeight: 700, marginLeft: 6, animation: "blink 1.2s infinite" }}>LIVE</span>}
                  </button>
                  <button className={`drawer-link-btn ${screen === "football" ? "active" : ""}`} onClick={() => { setScreen("football"); setMobileMenuOpen(false); }}>⚽ Football</button>
                  <button className={`drawer-link-btn ${screen === "fantasy11" ? "active" : ""}`} onClick={() => { setScreen("fantasy11"); setMobileMenuOpen(false); }}>🏆 Fantasy 11</button>
                  <button className={`drawer-link-btn ${screen === "leaderboard" ? "active" : ""}`} onClick={() => { fetchLeaderboard(); setScreen("leaderboard"); setMobileMenuOpen(false); }}>📊 Leaderboard</button>
                  <button className={`drawer-link-btn ${screen === "history" ? "active" : ""}`} onClick={() => { fetchAllHistory(); setScreen("history"); setMobileMenuOpen(false); }}>📜 Bet History</button>
                  <button className={`drawer-link-btn ${screen === "multiplayer" ? "active" : ""}`} onClick={() => { setScreen("multiplayer"); setMobileMenuOpen(false); }}>⚔️ Multiplayer</button>
                  <button className={`drawer-link-btn ${screen === "mines" ? "active" : ""}`} onClick={() => { setScreen("mines"); setMobileMenuOpen(false); }}>💣 Mines Game</button>
                  <button className={`drawer-link-btn ${screen === "shop" ? "active" : ""}`} onClick={() => { setScreen("shop"); setMobileMenuOpen(false); }}>💎 Shop & Rewards</button>
                  <button className={`drawer-link-btn ${screen === "odds-api" ? "active" : ""}`} onClick={() => { setScreen("odds-api"); setMobileMenuOpen(false); }}>⚙️ Odds API Setup</button>
                  <button className={`drawer-link-btn ${screen === "profile" ? "active" : ""}`} onClick={() => { setScreen("profile"); setMobileMenuOpen(false); }}>👤 Profile & Theme</button>
                  {username === "admin" && (
                    <button className={`drawer-link-btn ${screen === "admin" ? "active" : ""}`} onClick={() => { setScreen("admin"); setMobileMenuOpen(false); }}>🛡️ Admin Portal</button>
                  )}

                  <div className="drawer-footer-sec">
                    <button className="drawer-logout-btn" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>🚪 Logout</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── HOME ── */}
          {screen === "home" && (
            <div className="screen home-screen">
              <div style={{ width: "100%", overflow: "hidden", borderRadius: 16, marginBottom: 32, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <img src="/banner1.png" style={{ width: "100%", display: "block", objectFit: "cover" }} alt="CrickWager Banner" />
              </div>
              <div className="hero">
                <div className="hero-badge">🏆 FANTASY SPORTS GAMING</div>
                <h1 className="hero-title">Welcome,<br /><span className="accent">{username}!</span></h1>
                <p className="hero-sub">
                  You have <strong>{displayPoints.toLocaleString()} points</strong> available
                  {lockedPoints > 0 && <span> · <span style={{ color: "#BA7517" }}>🔒 {lockedPoints} locked</span></span>}
                </p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
                  <button className="btn-primary" onClick={() => setScreen("matches")}>🏏 Play on IPL →</button>
                  <button className="btn-primary" onClick={() => setScreen("fantasy11")} style={{ background: "#BA7517" }}>🏆 Fantasy 11 →</button>
                  <button className="btn-primary" onClick={() => { fetchAllHistory(); setScreen("history"); }} style={{ background: "#1D9E75" }}>My Bets →</button>
                  <button className="btn-primary" onClick={() => setScreen("mines")} style={{ background: "#E24B4A" }}>💣 Mines →</button>
                </div>
              </div>

              <PointsProgressCard points={points} lockedPoints={lockedPoints} allHistory={allHistory} />
              <FeaturedMatchBanner onBetClick={() => setScreen("matches")} onFantasyClick={() => setScreen("fantasy11")} />

              <div className="sports-grid">
                {SPORTS.map(sport => (
                  <div
                    key={sport.id}
                    className="sport-card"
                    onClick={() => { if (sport.available) setScreen(sport.id === "football" ? "football" : "matches"); }}
                    style={{
                      background: `linear-gradient(145deg, ${sport.gradientFrom} 0%, ${sport.gradientTo} 100%)`,
                      border: `1px solid ${sport.accentColor}33`,
                      boxShadow: sport.available ? `0 4px 24px ${sport.glowColor}` : "none",
                      cursor: sport.available ? "pointer" : "default",
                      opacity: sport.available ? 1 : 0.75,
                      position: "relative", overflow: "hidden",
                      transition: "transform 0.18s, box-shadow 0.18s",
                    }}
                    onMouseEnter={e => {
                      if (sport.available) {
                        e.currentTarget.style.transform = "translateY(-3px)";
                        e.currentTarget.style.boxShadow = `0 8px 32px ${sport.glowColor}`;
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = sport.available ? `0 4px 24px ${sport.glowColor}` : "none";
                    }}
                  >
                    <div style={{ position: "absolute", inset: 0, opacity: 0.05, backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "16px 16px", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: sport.available ? `linear-gradient(90deg, ${sport.accentColor}88, ${sport.accentColor})` : "rgba(255,255,255,0.1)", borderRadius: "12px 12px 0 0" }} />
                    <span className="sport-emoji" style={{ fontSize: 36, display: "block", marginBottom: 8, position: "relative" }}>{sport.emoji}</span>
                    <span className="sport-name" style={{ position: "relative", color: "#fff", fontWeight: 700 }}>{sport.name}</span>
                    <span style={{ display: "block", fontSize: 11, color: sport.available ? `${sport.accentColor}cc` : "rgba(255,255,255,0.3)", marginTop: 3, position: "relative" }}>{sport.tagline}</span>
                    {sport.available ? (
                      <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 5, background: `${sport.accentColor}22`, border: `1px solid ${sport.accentColor}44`, borderRadius: 99, padding: "4px 12px", fontSize: 11, color: sport.accentColor, fontWeight: 700, position: "relative" }}>
                        Play Now →
                      </div>
                    ) : (
                      <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 99, padding: "4px 12px", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.35)", position: "relative", letterSpacing: "0.04em" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "inline-block", flexShrink: 0 }} />
                        Coming Soon
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {allHistory.length > 0 && <RecentActivityFeed allHistory={allHistory} />}
            </div>
          )}

          {/* ── IPL MATCHES ── */}
          {screen === "matches" && (
            <div className="screen">
              <Matches onBetOnMatch={handleBetOnMatch} onFantasy11={handleFantasy11} />
            </div>
          )}

          {/* ── FOOTBALL MATCHES ── */}
          {screen === "football" && (
            <div className="screen">
              <Matches onBetOnMatch={handleBetOnMatch} onFantasy11={handleFantasy11} defaultSportTab="epl" />
            </div>
          )}

          {/* ── BET SCREEN ── */}
          {screen === "bet" && (
            isLiveInPlay ? (
              <LiveInPlayDashboard
                match={prefilledMatch}
                username={username}
                points={points}
                setPoints={setPoints}
                setLockedPoints={setLockedPoints}
                onClose={() => { setPrefilledMatch(null); setScreen("matches"); }}
                fetchMyBets={fetchMyBets}
                fetchAllHistory={fetchAllHistory}
                fetchLeaderboard={fetchLeaderboard}
                toast={toast}
              />
            ) : (
              <div className="screen bet-screen">
                <h2 className="screen-title">Place Your Bet</h2>
                {!prefilledMatch ? (
                  <div style={{ textAlign: "center", padding: 40 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>
                      {prefilledMatch?.matchId?.startsWith("football-") || prefilledMatch?.matchId?.startsWith("epl-") || prefilledMatch?.matchId?.startsWith("ucl-") ? "⚽" : "🏏"}
                    </div>
                    <p style={{ color: "var(--muted)", marginBottom: 20 }}>Pick a match first, then come back to bet!</p>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                      <button className="btn-primary" onClick={() => setScreen("matches")}>🏏 Browse IPL →</button>
                      <button className="btn-primary" onClick={() => setScreen("football")} style={{ background: "#3b82f6" }}>⚽ Browse Football →</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* ── Selected match banner ── */}
                    <div style={{ background: "rgba(29,158,117,0.1)", border: "1px solid #1D9E75", borderRadius: 12, padding: "12px 16px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#1D9E75", fontWeight: 600, marginBottom: 4, textTransform: "uppercase" }}>Selected Match</div>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>
                          {prefilledMatch.matchId?.startsWith("football-") || prefilledMatch.matchId?.startsWith("epl-") || prefilledMatch.matchId?.startsWith("ucl-") ? "⚽" : "🏏"} {prefilledMatch.matchLabel}
                          {matchStatus === "live" && (
                            <span style={{ marginLeft: 10, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "#FCEBEB", color: "#A32D2D", border: "0.5px solid #F09595" }}>● LIVE</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => { setPrefilledMatch(null); setSelectedSport(null); setSelectedTeam(null); setMatchStatus(null); }}
                        style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 20 }}
                      >×</button>
                    </div>

                    {/* ── Betting locked banner ── */}
                    {isBettingLocked && (
                      <div style={{ background: "#FCEBEB", border: "0.5px solid #F09595", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#F7C1C1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 18 }}>🔒</div>
                        <div>
                          <div style={{ fontWeight: 600, color: "#A32D2D", marginBottom: 4, fontSize: 14 }}>Bets are locked for this match</div>
                          <div style={{ fontSize: 13, color: "#993C1D", lineHeight: 1.5 }}>
                            {matchStatus === "live" ? "This match has already started." : "This match has already ended."} Go back and pick an upcoming match.
                          </div>
                          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                            <button className="btn-primary" style={{ padding: "8px 16px", fontSize: 13 }} onClick={() => setScreen("matches")}>🏏 IPL Matches →</button>
                            <button className="btn-primary" style={{ padding: "8px 16px", fontSize: 13, background: "#3b82f6" }} onClick={() => setScreen("football")}>⚽ Football →</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Bet form ── */}
                    {!isBettingLocked && (
                      <>
                        <div className="section">
                          <div className="section-label">Pick Your Team</div>
                          <div className="team-grid">
                            {selectedSport?.teams.map(team => {
                              const teamOdds = prefilledMatch?.odds?.[team];
                              const isSelected = selectedTeam === team;
                              const isFav = teamOdds && prefilledMatch?.odds
                                ? teamOdds === Math.min(...Object.values(prefilledMatch.odds))
                                : false;
                              return (
                                <button key={team} className={`team-card ${isSelected ? "selected" : ""}`} onClick={() => setSelectedTeam(team)}>
                                  <span>{team}</span>
                                  {teamOdds && (
                                    <span style={{ display: "block", fontSize: 12, fontWeight: 700, marginTop: 5, color: isFav ? "#0F6E56" : "#A32D2D", background: isFav ? "#E1F5EE" : "#FCEBEB", borderRadius: 6, padding: "2px 8px", border: `1px solid ${isFav ? "#5DCAA5" : "#F09595"}` }}>
                                      {teamOdds}x {isFav ? "⭐ Fav" : "💎 Dog"}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {selectedTeam && (
                          <div className="section">
                            {prefilledMatch?.odds?.[selectedTeam] && betAmount && parseInt(betAmount) > 0 && (
                              <div style={{ background: "#E1F5EE", border: "1px solid #5DCAA5", borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <div style={{ fontSize: 11, color: "#085041", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Potential Win</div>
                                  <div style={{ fontSize: 26, fontWeight: 700, color: "#0F6E56", lineHeight: 1 }}>{Math.floor(parseInt(betAmount) * prefilledMatch.odds[selectedTeam])} pts</div>
                                  <div style={{ fontSize: 12, color: "#1D9E75", marginTop: 4 }}>{parseInt(betAmount)} pts × {prefilledMatch.odds[selectedTeam]}x odds</div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ fontSize: 11, color: "#888780", marginBottom: 2 }}>Profit if win</div>
                                  <div style={{ fontSize: 18, fontWeight: 700, color: "#0F6E56" }}>+{Math.floor(parseInt(betAmount) * prefilledMatch.odds[selectedTeam]) - parseInt(betAmount)} pts</div>
                                  <div style={{ fontSize: 10, color: "#888780", marginTop: 4 }}>📊 Live odds</div>
                                </div>
                              </div>
                            )}
                            <div className="section-label">Bet Amount (Available: {points} pts)</div>
                            <div className="amount-row">
                              {[50, 100, 250, 500].map(amt => (
                                <button key={amt} className="amount-chip" onClick={() => setBetAmount(String(Math.min(amt, points)))}>{amt}</button>
                              ))}
                              <button className="amount-chip all-in" onClick={() => setBetAmount(String(points))}>ALL IN 🔥</button>
                            </div>
                            <div className="input-row">
                              <input
                                type="number" className="bet-input" placeholder="Enter amount..."
                                value={betAmount} onChange={e => setBetAmount(e.target.value)} max={points}
                              />
                              <button
                                className="btn-primary bet-btn" onClick={placeBet}
                                disabled={loading || !betAmount || parseInt(betAmount) <= 0 || parseInt(betAmount) > points}
                              >
                                {loading ? <LoadingDots /> : "Lock Bet 🔒"}
                              </button>
                            </div>
                            {error && <div className="error-msg">{error}</div>}
                          </div>
                        )}
                      </>
                    )}

                    {isBettingLocked && error && <div className="error-msg">{error}</div>}

                    <BetLockedModal
                      isOpen={!!betPlaced}
                      onClose={() => setBetPlaced(null)}
                      themeId={betPlaced?.themeId}
                      betAmount={betPlaced?.amount}
                      teamName={betPlaced?.team}
                      matchLabel={betPlaced?.matchLabel}
                      potentialWin={betPlaced?.potentialWin}
                      odds={betPlaced?.odds + "x"}
                    />
                  </>
                )}
              </div>
            )
          )}

          {/* ── FANTASY 11 SCREEN ── */}
          {screen === "fantasy11" && (
            <div className="screen">
              <Fantasy11
                username={username}
                points={points}
                setPoints={setPoints}
                matchInfo={prefilledMatch}
                matchStatus={matchStatus}
              />
            </div>
          )}

          {/* ── LEADERBOARD ── */}
          {screen === "leaderboard" && (
            <div className="screen leaderboard-screen">
              <h2 className="screen-title">🏆 Leaderboard</h2>
              <button className="btn-primary" style={{ marginBottom: 20, padding: "0.5rem 1.5rem" }} onClick={fetchLeaderboard}>🔄 Refresh</button>
              {leaderboard.length === 0 ? (
                <div className="empty-state">Loading players...</div>
              ) : (
                <div className="leaderboard">
                  {leaderboardList().map((player, i) => (
                    <div key={player.name} className={`leaderboard-row ${player.name.includes("(You)") ? "you" : ""}`}>
                      <div className="rank">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</div>
                      <div className="player-name">{player.name}</div>
                      <div className="player-points">{player.points.toLocaleString()} pts</div>
                      <div className="points-bar"><div className="points-fill" style={{ width: `${(player.points / maxPoints) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── HISTORY ── */}
          {screen === "history" && (
            <div className="screen history-screen">
              <h2 className="screen-title">📜 My History</h2>
              <button className="btn-primary" style={{ marginBottom: 16, padding: "0.5rem 1.5rem" }} onClick={fetchAllHistory}>🔄 Refresh</button>
              <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
                {historyTabs.map(tab => (
                  <button key={tab.key} onClick={() => setHistoryFilter(tab.key)} style={{
                    fontSize: 12, padding: "5px 14px", borderRadius: 99, cursor: "pointer",
                    border: `1px solid ${historyFilter === tab.key ? tab.color : tab.color + "44"}`,
                    background: historyFilter === tab.key ? tab.color + "22" : "transparent",
                    color: historyFilter === tab.key ? tab.color : tab.color + "99",
                    fontWeight: historyFilter === tab.key ? 700 : 400,
                    transition: "all 0.15s",
                  }}>
                    {tab.emoji} {tab.label} ({tab.key === "all" ? allHistory.length : allHistory.filter(h => h.type === tab.key).length})
                  </button>
                ))}
              </div>

              {filteredHistory.length === 0 ? (
                <div className="empty-state">
                  {historyFilter === "fantasy11"
                    ? "No Fantasy 11 squads yet! Go to 🏏 IPL tab and click Fantasy 11 on a match."
                    : historyFilter === "bet"
                      ? "No bets yet! Go to 🏏 IPL or ⚽ Football and click Bet Now."
                      : "No entries yet! Go to 🏏 IPL, ⚽ Football, ⚔️ Multiplayer, or join a contest 🎯"
                  }
                </div>
              ) : (
                <div className="history-list">
                  {filteredHistory.map((item) => (
                    <div
                      key={`${item.type}-${item._id}`}
                      className={`history-row ${item.status === "won" ? "win" : item.status === "lost" ? "lose" : ""}`}
                      onClick={() => setSelectedHistoryItem(item)}
                      style={{ borderLeft: `3px solid ${statusColor(item.status)}`, cursor: "pointer", transition: "background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}
                    >
                      <div className="history-sport">{statusEmoji(item.status)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{item.matchLabel}</span>
                          <span style={{
                            fontSize: 10, padding: "1px 7px", borderRadius: 99, fontWeight: 600,
                            background: item.type === "bet" ? "#BA751722" : item.type === "contest" ? "#1D9E7522" : item.type === "fantasy11" ? "#ffd16622" : "#7F77DD22",
                            color: item.type === "bet" ? "#BA7517" : item.type === "contest" ? "#1D9E75" : item.type === "fantasy11" ? "#8a6a00" : "#7F77DD",
                          }}>
                            {item.typeEmoji} {item.typeLabel}
                          </span>
                          {item.type === "bet" && item.odds && item.odds !== 2.0 && (
                            <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 99, fontWeight: 600, background: "#E1F5EE", color: "#085041", border: "0.5px solid #5DCAA5" }}>{item.odds}x odds</span>
                          )}
                          <span style={{ fontSize: 9, color: "#7d8590", marginLeft: "auto" }}>tap for details →</span>
                        </div>
                        {item.type === "fantasy11" ? (
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            <span style={{ opacity: 0.7 }}>{item.team}</span>
                            {item.players?.length > 0 && <span style={{ marginLeft: 8, opacity: 0.5, fontSize: 11 }}>· {item.players.length} players selected</span>}
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                            Picked: <strong>{item.team}</strong>
                            {item.type === "contest" && item.contestName && <span style={{ marginLeft: 6, opacity: 0.6 }}>· {item.contestName}</span>}
                            {item.type === "challenge" && item.opponent && <span style={{ marginLeft: 6, opacity: 0.6 }}>· vs {item.opponent}</span>}
                          </div>
                        )}
                        {item.detail && (
                          <div style={{
                            fontSize: 11, marginTop: 3,
                            color: item.type === "fantasy11" && item.fantasyPoints !== null ? "#ffd166" : "rgba(255,255,255,0.4)",
                            fontWeight: item.type === "fantasy11" && item.fantasyPoints !== null ? 700 : 400,
                          }}>
                            {item.type === "fantasy11" && item.fantasyPoints !== null ? "🏏 " : ""}{item.detail}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{
                          color: item.type === "fantasy11" ? (item.fantasyPoints !== null ? "#ffd166" : "#888780") : statusColor(item.status),
                          fontWeight: 600, fontSize: 14, marginBottom: 4,
                        }}>
                          {pointsDisplay(item)}
                        </div>
                        <div className="history-badge" style={{ background: `${statusColor(item.status)}22`, color: statusColor(item.status) }}>
                          {statusLabel(item.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PROFILE ── */}
          {screen === "profile" && (
            <Profile
              username={username}
              points={points}
              lockedPoints={lockedPoints}
              allHistory={allHistory}
              onNavigate={setScreen}
              onThemeChange={setUserTheme}
              currentTheme={userTheme}
            />
          )}

          {/* ── MULTIPLAYER ── */}
          {screen === "multiplayer" && (
            <div className="screen">
              <Multiplayer username={username} points={points} setPoints={setPoints} />
            </div>
          )}

          {/* ── MINES ── */}
          {screen === "mines" && (
            <div className="screen">
              <Mines username={username} points={points} setPoints={setPoints} />
            </div>
          )}

          {/* ── ADMIN PANEL ── */}
          {screen === "admin" && (
            <div className="screen" style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <AdminPanel onBack={() => setScreen("home")} />
            </div>
          )}

          {/* ── SHOP ── */}
          {screen === "shop" && (
            <div className="screen">
              <Shop username={username} />
            </div>
          )}

          {/* ── ODDS API HUB ── */}
          {screen === "odds-api" && (
            <div className="screen">
              <OddsApiHub />
            </div>
          )}
        </>
      )}

      {/* ── MOBILE BOTTOM NAV ── */}
      {screen !== "auth" && screen !== "admin" && (
        <nav className="bottom-nav">
          <button className={`bottom-nav-btn ${screen === "home" ? "active" : ""}`} onClick={() => setScreen("home")}>
            <span className="bottom-nav-icon">🏠</span>
            <span className="bottom-nav-label">Home</span>
          </button>
          <button className={`bottom-nav-btn ${screen === "matches" ? "active" : ""}`} onClick={() => setScreen("matches")} style={{ position: "relative" }}>
            <span className="bottom-nav-icon">🏏</span>
            <span className="bottom-nav-label">IPL</span>
            {globalLiveCount > 0 && <span className="bottom-nav-live-dot" />}
          </button>
          <button className={`bottom-nav-btn ${screen === "football" ? "active" : ""}`} onClick={() => setScreen("football")}>
            <span className="bottom-nav-icon">⚽</span>
            <span className="bottom-nav-label">Football</span>
          </button>
          <button className={`bottom-nav-btn ${screen === "fantasy11" ? "active" : ""}`} onClick={() => setScreen("fantasy11")}>
            <span className="bottom-nav-icon">🏆</span>
            <span className="bottom-nav-label">Fantasy</span>
          </button>
          <button className={`bottom-nav-btn ${screen === "multiplayer" ? "active" : ""}`} onClick={() => setScreen("multiplayer")}>
            <span className="bottom-nav-icon">⚔️</span>
            <span className="bottom-nav-label">Multi</span>
          </button>
          <button className={`bottom-nav-btn ${screen === "history" ? "active" : ""}`} onClick={() => { fetchAllHistory(); setScreen("history"); }}>
            <span className="bottom-nav-icon">📜</span>
            <span className="bottom-nav-label">History</span>
            {pendingCount > 0 && <span className="bottom-nav-badge">{pendingCount}</span>}
          </button>
          <button className={`bottom-nav-btn ${screen === "profile" ? "active" : ""}`} onClick={() => setScreen("profile")}>
            <span className="bottom-nav-icon">👤</span>
            <span className="bottom-nav-label">Profile</span>
          </button>
          <button className={`bottom-nav-btn ${screen === "mines" ? "active" : ""}`} onClick={() => setScreen("mines")}>
            <span className="bottom-nav-icon">💣</span>
            <span className="bottom-nav-label">Mines</span>
          </button>
        </nav>
      )}

      {/* ── THEME MUSIC PLAYER ── */}
      {screen !== "auth" && screen !== "admin" && <ThemeMusicPlayer />}
    </div>
  );
}
// ← single closing brace — end of export default function App()
